import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';

import { Canvases } from '@air/api';
import { useToasts } from '@air/provider-toast';
import { callAirApi } from '@air/utils-api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type CanvasListResponse } from '~/swr-hooks/canvases/types';
import { getCanvasDetailKey } from '~/swr-hooks/canvases/useCanvas';
import { isCanvasesListKey } from '~/swr-hooks/canvases/useListCanvases';

export type UsePrivateCanvasesDeleteCanvasMutationParams = Extract<
  NonNullable<Parameters<typeof Canvases.delete>[0]>,
  { canvasIds: string[] }
>;

export const usePrivateCanvasesDeleteCanvasMutation = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToasts();

  return useMutation({
    mutationFn: (params: UsePrivateCanvasesDeleteCanvasMutationParams) => callAirApi(() => Canvases.delete(params)),
    onSuccess: (_, params) => {
      const { canvasIds, workspaceId } = params;
      const deletedSet = new Set(canvasIds);

      for (const canvasId of canvasIds) {
        queryClient.removeQueries({
          queryKey: getCanvasDetailKey({ canvasId, workspaceId }),
        });
      }

      queryClient.setQueriesData<InfiniteData<CanvasListResponse>>(
        {
          predicate: (query) => isCanvasesListKey(query.queryKey),
        },
        (oldData) => {
          if (!oldData) {
            return oldData;
          }
          let totalRemoved = 0;
          const newPages = oldData.pages.map((page) => {
            const filteredData = page.data.filter((canvas) => !deletedSet.has(canvas.id));
            totalRemoved += page.data.length - filteredData.length;
            return {
              ...page,
              data: filteredData,
            };
          });
          const globalTotal = Math.max((oldData.pages[0]?.total ?? 0) - totalRemoved, 0);

          return {
            ...oldData,
            pages: newPages.map((page, i) => (i === 0 ? { ...page, total: globalTotal } : page)),
          };
        },
      );
    },
    onError: (error, params) => {
      showToast('There was an error deleting the Canvas.');
      reportErrorToBugsnag({
        error,
        context: 'Failed to delete canvas',
        metadata: { canvasIds: params.canvasIds },
      });
    },
  });
};
